import { ChangeEvent, useEffect, useId, useMemo, useRef, useState } from "react";
import "./DocumentIntelligenceSourcePicker.css";

export type DocumentIntelligenceSourceTab =
  | "recent"
  | "library"
  | "projects"
  | "templates"
  | "generated"
  | "review-tables"
  | "repository"
  | "sharepoint";

export type DocumentIntelligenceSourceAvailability = {
  status: "available" | "unavailable" | "loading";
  message?: string;
  actionLabel?: string;
};

export type DocumentIntelligenceSourceItem = {
  id: string;
  name: string;
  tab: DocumentIntelligenceSourceTab;
  type: string;
  source: string;
  workspaceId?: string | null;
  versionId?: string | null;
  detail?: string;
  modifiedAt?: string | null;
  disabled?: boolean;
  disabledReason?: string;
};

export type DocumentIntelligenceSourceSort =
  | "name-asc"
  | "name-desc"
  | "modified-desc"
  | "modified-asc";

export type DocumentIntelligenceSourcePickerProps = {
  items: DocumentIntelligenceSourceItem[];
  availability: Record<DocumentIntelligenceSourceTab, DocumentIntelligenceSourceAvailability>;
  initialSelectedIds?: string[];
  initialTab?: DocumentIntelligenceSourceTab;
  busy?: boolean;
  disabled?: boolean;
  title?: string;
  confirmLabel?: string;
  compact?: boolean;
  visibleTabs?: DocumentIntelligenceSourceTab[];
  onUploadFiles: (files: File[]) => void | Promise<void>;
  onUploadFolder: (files: File[]) => void | Promise<void>;
  onAvailabilityAction?: (tab: DocumentIntelligenceSourceTab) => void;
  onCancel: () => void;
  onConfirm: (items: DocumentIntelligenceSourceItem[]) => void | Promise<void>;
};

const tabs: Array<{ id: DocumentIntelligenceSourceTab; label: string }> = [
  { id: "recent", label: "Recent" },
  { id: "library", label: "Library" },
  { id: "projects", label: "Projects" },
  { id: "templates", label: "Templates" },
  { id: "generated", label: "Generated" },
  { id: "review-tables", label: "Review Tables" },
  { id: "repository", label: "Repository" },
  { id: "sharepoint", label: "SharePoint" },
];

function normalized(value: string): string {
  return value.trim().toLocaleLowerCase();
}

function dateValue(value?: string | null): number {
  if (!value) return 0;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function filterAndSortDocumentIntelligenceSources(
  items: DocumentIntelligenceSourceItem[],
  options: {
    tab: DocumentIntelligenceSourceTab;
    query?: string;
    source?: string;
    type?: string;
    sort?: DocumentIntelligenceSourceSort;
  }
): DocumentIntelligenceSourceItem[] {
  const query = normalized(options.query || "");
  const source = normalized(options.source || "");
  const type = normalized(options.type || "");
  const sort = options.sort || "modified-desc";

  return items
    .filter((item) => item.tab === options.tab)
    .filter((item) => !source || normalized(item.source) === source)
    .filter((item) => !type || normalized(item.type) === type)
    .filter((item) => {
      if (!query) return true;
      return normalized(`${item.name} ${item.detail || ""} ${item.source} ${item.type}`).includes(query);
    })
    .sort((left, right) => {
      if (sort === "name-asc") return left.name.localeCompare(right.name);
      if (sort === "name-desc") return right.name.localeCompare(left.name);
      const delta = dateValue(left.modifiedAt) - dateValue(right.modifiedAt);
      return sort === "modified-asc" ? delta : -delta;
    });
}

export function sourcePickerFilterValues(
  items: DocumentIntelligenceSourceItem[],
  tab: DocumentIntelligenceSourceTab,
  key: "source" | "type"
): string[] {
  return Array.from(new Set(
    items
      .filter((item) => item.tab === tab)
      .map((item) => item[key].trim())
      .filter(Boolean)
  )).sort((left, right) => left.localeCompare(right));
}

function formatModified(value?: string | null): string {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

export function DocumentIntelligenceSourcePicker({
  items,
  availability,
  initialSelectedIds = [],
  initialTab = "recent",
  busy = false,
  disabled = false,
  title = "Add files and sources",
  confirmLabel = "Use selected",
  compact = false,
  visibleTabs,
  onUploadFiles,
  onUploadFolder,
  onAvailabilityAction,
  onCancel,
  onConfirm,
}: DocumentIntelligenceSourcePickerProps) {
  const headingId = useId();
  const searchId = useId();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const folderInputRef = useRef<HTMLInputElement | null>(null);
  const [activeTab, setActiveTab] = useState<DocumentIntelligenceSourceTab>(initialTab);
  const [query, setQuery] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [sort, setSort] = useState<DocumentIntelligenceSourceSort>("modified-desc");
  const [selectedIds, setSelectedIds] = useState<string[]>(initialSelectedIds);
  const renderedTabs = tabs.filter((tab) => !visibleTabs || visibleTabs.includes(tab.id));

  useEffect(() => {
    setQuery("");
    setSourceFilter("");
    setTypeFilter("");
  }, [activeTab]);

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const selectedItems = useMemo(
    () => selectedIds.map((id) => items.find((item) => item.id === id)).filter(
      (item): item is DocumentIntelligenceSourceItem => Boolean(item)
    ),
    [items, selectedIds]
  );
  const visibleItems = useMemo(
    () => filterAndSortDocumentIntelligenceSources(items, {
      tab: activeTab,
      query,
      source: sourceFilter,
      type: typeFilter,
      sort,
    }),
    [activeTab, items, query, sort, sourceFilter, typeFilter]
  );
  const sourceOptions = useMemo(
    () => sourcePickerFilterValues(items, activeTab, "source"),
    [activeTab, items]
  );
  const typeOptions = useMemo(
    () => sourcePickerFilterValues(items, activeTab, "type"),
    [activeTab, items]
  );
  const tabState = availability[activeTab];
  const canBrowseTab = tabState.status === "available";
  const selectableVisibleItems = visibleItems.filter((item) => !item.disabled);
  const allVisibleSelected = selectableVisibleItems.length > 0
    && selectableVisibleItems.every((item) => selectedSet.has(item.id));

  const toggleItem = (item: DocumentIntelligenceSourceItem) => {
    if (item.disabled || disabled || busy || !canBrowseTab) return;
    setSelectedIds((current) => current.includes(item.id)
      ? current.filter((id) => id !== item.id)
      : [...current, item.id]);
  };

  const handleUpload = async (
    event: ChangeEvent<HTMLInputElement>,
    callback: (files: File[]) => void | Promise<void>
  ) => {
    const files = Array.from(event.currentTarget.files || []);
    event.currentTarget.value = "";
    if (files.length) await callback(files);
  };

  return (
    <section className={`docint-source-picker ${compact ? "docint-source-picker--compact" : ""}`} aria-labelledby={headingId}>
      <header className="docint-source-picker__header">
        <div>
          <h2 id={headingId}>{title}</h2>
          <p>{compact ? "Choose files for this activity." : "Choose the exact items that should be available to this request."}</p>
        </div>
        {!compact ? <div className="docint-source-picker__upload-actions">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            hidden
            onChange={(event) => { void handleUpload(event, onUploadFiles); }}
          />
          <input
            ref={folderInputRef}
            type="file"
            multiple
            hidden
            {...({ webkitdirectory: "", directory: "" } as Record<string, string>)}
            onChange={(event) => { void handleUpload(event, onUploadFolder); }}
          />
          <button
            type="button"
            className="docint-source-picker__button"
            disabled={disabled || busy}
            onClick={() => fileInputRef.current?.click()}
          >
            Upload files
          </button>
          <button
            type="button"
            className="docint-source-picker__button"
            disabled={disabled || busy}
            onClick={() => folderInputRef.current?.click()}
          >
            Upload folder
          </button>
        </div> : null}
      </header>

      {renderedTabs.length > 1 ? <div className="docint-source-picker__tabs" role="tablist" aria-label="Source locations">
        {renderedTabs.map((tab) => {
          const state = availability[tab.id];
          return (
            <button
              key={tab.id}
              id={`${headingId}-${tab.id}-tab`}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              aria-controls={`${headingId}-${tab.id}-panel`}
              className="docint-source-picker__tab"
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
              {state.status !== "available" ? (
                <span
                  className={`docint-source-picker__availability docint-source-picker__availability--${state.status}`}
                  aria-label={`${state.status}: ${state.message || "Availability not confirmed"}`}
                  title={state.message || "Availability not confirmed"}
                />
              ) : null}
            </button>
          );
        })}
      </div> : null}

      <div
        id={`${headingId}-${activeTab}-panel`}
        role={renderedTabs.length > 1 ? "tabpanel" : "region"}
        aria-labelledby={renderedTabs.length > 1 ? `${headingId}-${activeTab}-tab` : headingId}
        className="docint-source-picker__body"
      >
        <div className="docint-source-picker__browser">
          <div className="docint-source-picker__filters">
            <div className="docint-source-picker__search">
              <label htmlFor={searchId}>Search</label>
              <input
                id={searchId}
                type="search"
                value={query}
                disabled={!canBrowseTab || disabled}
                placeholder={`Search ${tabs.find((tab) => tab.id === activeTab)?.label || "sources"}`}
                onChange={(event) => setQuery(event.target.value)}
              />
            </div>
            {!compact ? <label>
              Source
              <select
                value={sourceFilter}
                disabled={!canBrowseTab || disabled}
                onChange={(event) => setSourceFilter(event.target.value)}
              >
                <option value="">All sources</option>
                {sourceOptions.map((source) => <option key={source} value={source}>{source}</option>)}
              </select>
            </label> : null}
            {!compact ? <label>
              Type
              <select
                value={typeFilter}
                disabled={!canBrowseTab || disabled}
                onChange={(event) => setTypeFilter(event.target.value)}
              >
                <option value="">All types</option>
                {typeOptions.map((type) => <option key={type} value={type}>{type}</option>)}
              </select>
            </label> : null}
            {!compact ? <label>
              Sort
              <select
                value={sort}
                disabled={!canBrowseTab || disabled}
                onChange={(event) => setSort(event.target.value as DocumentIntelligenceSourceSort)}
              >
                <option value="modified-desc">Recently updated</option>
                <option value="modified-asc">Oldest updated</option>
                <option value="name-asc">Name A-Z</option>
                <option value="name-desc">Name Z-A</option>
              </select>
            </label> : null}
          </div>

          {!canBrowseTab ? (
            <div className="docint-source-picker__empty" role="status">
              <strong>{tabState.status === "loading" ? "Checking availability" : "Source unavailable"}</strong>
              <p>{tabState.message || "This source cannot be used right now."}</p>
              {tabState.actionLabel && onAvailabilityAction ? (
                <button
                  type="button"
                  className="docint-source-picker__button"
                  onClick={() => onAvailabilityAction(activeTab)}
                >
                  {tabState.actionLabel}
                </button>
              ) : null}
            </div>
          ) : (
            <>
              <div className="docint-source-picker__list-toolbar">
                <button
                  type="button"
                  className="docint-source-picker__link-button"
                  disabled={!selectableVisibleItems.length || disabled || busy}
                  onClick={() => setSelectedIds((current) => {
                    if (allVisibleSelected) {
                      const visibleIds = new Set(selectableVisibleItems.map((item) => item.id));
                      return current.filter((id) => !visibleIds.has(id));
                    }
                    return Array.from(new Set([...current, ...selectableVisibleItems.map((item) => item.id)]));
                  })}
                >
                  {allVisibleSelected ? "Clear visible" : "Select visible"}
                </button>
                <span aria-live="polite">{visibleItems.length} result{visibleItems.length === 1 ? "" : "s"}</span>
              </div>
              <div className="docint-source-picker__list" aria-label="Available sources">
                {visibleItems.map((item) => {
                  const checked = selectedSet.has(item.id);
                  return (
                    <label
                      key={item.id}
                      className={`docint-source-picker__item ${item.disabled ? "docint-source-picker__item--disabled" : ""}`}
                      title={item.disabledReason}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={item.disabled || disabled || busy}
                        onChange={() => toggleItem(item)}
                      />
                      <span className="docint-source-picker__item-text">
                        <strong>{item.name}</strong>
                        <span>{[item.source, item.type, item.detail, formatModified(item.modifiedAt)].filter(Boolean).join(" | ")}</span>
                        {item.disabledReason ? <span className="docint-source-picker__item-warning">{item.disabledReason}</span> : null}
                      </span>
                    </label>
                  );
                })}
                {!visibleItems.length ? (
                  <div className="docint-source-picker__empty">
                    <strong>No matching items</strong>
                    <p>Adjust the search or filters, or upload files to continue.</p>
                  </div>
                ) : null}
              </div>
            </>
          )}
        </div>

        {!compact ? <aside className="docint-source-picker__selection" aria-label="Selected sources">
          <div className="docint-source-picker__selection-heading">
            <h3>Selected</h3>
            <span aria-live="polite">{selectedItems.length}</span>
          </div>
          <div className="docint-source-picker__chips">
            {selectedItems.map((item) => (
              <span className="docint-source-picker__chip" key={item.id}>
                <span title={item.name}>{item.name}</span>
                <button
                  type="button"
                  aria-label={`Remove ${item.name}`}
                  disabled={disabled || busy}
                  onClick={() => setSelectedIds((current) => current.filter((id) => id !== item.id))}
                >
                  {"\u00d7"}
                </button>
              </span>
            ))}
            {!selectedItems.length ? <p>No files or sources selected.</p> : null}
          </div>
        </aside> : null}
      </div>

      <footer className="docint-source-picker__footer">
        <span>{selectedItems.length} selected</span>
        <div>
          <button
            type="button"
            className="docint-source-picker__button"
            disabled={busy}
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className="docint-source-picker__button docint-source-picker__button--primary"
            disabled={disabled || busy || !selectedItems.length}
            onClick={() => { void onConfirm(selectedItems); }}
          >
            {busy ? "Saving..." : confirmLabel}
          </button>
        </div>
      </footer>
    </section>
  );
}

export default DocumentIntelligenceSourcePicker;
